import React, { useState, useCallback } from 'react';
import { Table2, Code2, RefreshCw, Play, Loader } from 'lucide-react';
import { API, apiFetch } from '../lib/api';

const TABLES = [
  { id: 'ClientMaster',         label: 'Clients' },
  { id: 'LicenseRegistry',      label: 'Licenses' },
  { id: 'ClientMaster_License', label: 'Client Licenses' },
  { id: 'UserSessions',         label: 'Sessions' },
  { id: 'ClientMaster_Child',   label: 'Client Users' },
  { id: 'tbl_industry_master',  label: 'Industries' },
  { id: 'LicensePricing',       label: 'Pricing' },
  { id: 'UAMS_Modules',         label: 'Modules' },
  { id: 'UAMS_Screens',         label: 'Screens' },
  { id: 'tbl_translations',     label: 'Translations' },
  { id: 'ClientMessages',       label: 'Messages' },
];

export default function SmartAssistancePanel() {
  const [view, setView] = useState('tables'); // 'tables' | 'sql'
  const [activeTable, setActiveTable] = useState(null);
  const [tableData, setTableData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sqlText, setSqlText] = useState('SELECT TOP 20 * FROM webapp.ClientMaster ORDER BY ClientId DESC');
  const [sqlResult, setSqlResult] = useState(null);
  const [sqlLoading, setSqlLoading] = useState(false);
  const [error, setError] = useState(null);

  const loadTable = useCallback(async (tableId) => {
    setActiveTable(tableId);
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`${API}/admin/smart-assist/table-data`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tableId })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setTableData(d);
    } catch (e) { setError(e.message); setTableData(null); }
    finally { setLoading(false); }
  }, []);

  const runSql = useCallback(async () => {
    if (!sqlText.trim()) return;
    setSqlLoading(true);
    setError(null);
    setSqlResult(null);
    try {
      const res = await apiFetch(`${API}/admin/smart-assist/sql`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sql: sqlText })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error);
      setSqlResult(d);
    } catch (e) { setError(e.message); }
    finally { setSqlLoading(false); }
  }, [sqlText]);

  const data = view === 'sql' ? sqlResult : tableData;

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left pane — tabs + table list */}
      <div style={{
        width: 180, flexShrink: 0, borderRight: '1px solid var(--border)',
        background: 'var(--surface-2)', display: 'flex', flexDirection: 'column', overflow: 'hidden'
      }}>
        {/* Tab strip */}
        <div style={{ display: 'flex', borderBottom: '1px solid var(--border)', padding: '6px 8px 0', gap: 4 }}>
          {[
            { id: 'tables', icon: <Table2 size={11} />, label: 'Tables' },
            { id: 'sql',    icon: <Code2  size={11} />, label: 'SQL'    },
          ].map(t => (
            <button key={t.id} onClick={() => setView(t.id)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 8px', fontSize: 10, fontWeight: 700, cursor: 'pointer',
                border: 'none', borderRadius: '4px 4px 0 0',
                background: view === t.id ? 'var(--surface)' : 'transparent',
                color: view === t.id ? 'var(--text-primary)' : 'var(--text-secondary)',
                borderBottom: view === t.id ? '2px solid #3b82f6' : '2px solid transparent'
              }}>{t.icon}{t.label}</button>
          ))}
        </div>

        {/* Table list (only in Tables tab) */}
        {view === 'tables' && (
          <div style={{ flex: 1, overflowY: 'auto', padding: '6px 4px' }}>
            {TABLES.map(t => (
              <button key={t.id} onClick={() => loadTable(t.id)}
                data-testid={`assist-table-${t.id}`}
                style={{
                  display: 'block', width: '100%', textAlign: 'left',
                  padding: '5px 10px', fontSize: 11, cursor: 'pointer',
                  border: 'none', borderRadius: 4, marginBottom: 1,
                  background: activeTable === t.id ? 'rgba(59,130,246,0.12)' : 'transparent',
                  color: activeTable === t.id ? '#3b82f6' : 'var(--text-primary)',
                  fontWeight: activeTable === t.id ? 700 : 400,
                  transition: 'background 100ms'
                }}
              >{t.label}</button>
            ))}
          </div>
        )}
        {view === 'sql' && (
          <div style={{ padding: '8px 8px 4px', flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span style={{ fontSize: 9, color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>SELECT only · TOP 200 max</span>
            <button
              className="erp-btn erp-btn-primary"
              onClick={runSql}
              disabled={sqlLoading}
              data-testid="assist-run-sql"
              style={{ display: 'flex', alignItems: 'center', gap: 4, justifyContent: 'center', fontSize: 11 }}
            >
              {sqlLoading ? <Loader size={11} className="spin" /> : <Play size={11} />}
              {sqlLoading ? 'Running…' : 'Run Query'}
            </button>
          </div>
        )}
      </div>

      {/* Right pane — SQL editor (top) + results grid */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {view === 'sql' && (
          <textarea
            value={sqlText}
            onChange={e => setSqlText(e.target.value)}
            onKeyDown={e => { if (e.ctrlKey && e.key === 'Enter') { e.preventDefault(); runSql(); } }}
            data-testid="assist-sql-editor"
            spellCheck={false}
            style={{
              height: 90, resize: 'none', border: 'none', borderBottom: '1px solid var(--border)',
              padding: '8px 12px', fontFamily: 'var(--font-mono, monospace)', fontSize: 11,
              background: 'var(--surface-3)', color: 'var(--text-primary)', outline: 'none',
              flexShrink: 0
            }}
            placeholder="Type a SELECT query and press Ctrl+Enter to run…"
          />
        )}

        {/* Results */}
        <div style={{ flex: 1, overflow: 'auto', padding: 0 }}>
          {loading && (
            <div style={{ padding: 20, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12 }}>
              <Loader size={14} className="spin" style={{ marginRight: 6 }} />Loading…
            </div>
          )}
          {error && (
            <div style={{ padding: 12, color: '#ef4444', fontSize: 11, background: 'rgba(239,68,68,0.06)', borderBottom: '1px solid rgba(239,68,68,0.2)' }}>
              ⚠ {error}
            </div>
          )}
          {data && !loading && (
            <>
              <div style={{ padding: '4px 10px', fontSize: 10, color: 'var(--text-secondary)', background: 'var(--surface-2)', borderBottom: '1px solid var(--border)' }}>
                {data.rowCount ?? data.rows?.length ?? 0} rows · {data.columns?.length ?? 0} columns
                {data.rowCount >= 200 && <span style={{ color: '#f59e0b', marginLeft: 8 }}>⚠ capped at 200 rows</span>}
              </div>
              <ResultsTable columns={data.columns} rows={data.rows} />
            </>
          )}
          {!data && !loading && !error && view === 'tables' && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12 }}>
              Click a table on the left to inspect data
            </div>
          )}
          {!data && !loading && !error && view === 'sql' && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12 }}>
              Write a SELECT query and click Run (or Ctrl+Enter)
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultsTable({ columns = [], rows = [] }) {
  if (!columns.length) return <div style={{ padding: 16, fontSize: 12, color: 'var(--text-secondary)' }}>No data returned.</div>;
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ borderCollapse: 'collapse', width: 'max-content', minWidth: '100%', fontSize: 11 }}>
        <thead>
          <tr>
            {columns.map(c => (
              <th key={c} style={{
                padding: '4px 10px', background: 'var(--surface-3)', color: 'var(--text-primary)',
                borderBottom: '1px solid var(--border)', textAlign: 'left', fontWeight: 700,
                whiteSpace: 'nowrap', fontSize: 10, letterSpacing: '0.04em', textTransform: 'uppercase',
                position: 'sticky', top: 0, zIndex: 1
              }}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id || row._id || row.Id || i} style={{ background: i % 2 === 0 ? 'var(--surface)' : 'var(--surface-2)' }}>
              {columns.map(c => {
                const v = row[c];
                const display = v == null ? <span style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>NULL</span>
                  : v instanceof Date ? new Date(v).toLocaleString()
                  : typeof v === 'boolean' ? (v ? '✓' : '✗')
                  : String(v).length > 80 ? String(v).slice(0, 80) + '…'
                  : String(v);
                return (
                  <td key={c} title={v != null ? String(v) : ''} style={{
                    padding: '3px 10px', borderBottom: '1px solid var(--border)',
                    whiteSpace: 'nowrap', color: 'var(--text-primary)'
                  }}>{display}</td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
